import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { SearchableCombobox } from '../components/ui/searchable-combobox';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Skeleton } from '../components/ui/skeleton';
import {
    BookOpen,
    Plus,
    Search,
    Filter,
    MoreHorizontal,
    Eye,
    Pencil,
    Trash2,
    Building2,
    User,
    Calendar,
    Loader2,
    FileText,
    CheckCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const Editorial = () => {
    const { api, user, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    
    const [projects, setProjects] = useState([]);
    const [magazines, setMagazines] = useState([]);
    const [companies, setCompanies] = useState([]);
    const [contacts, setContacts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [magazineFilter, setMagazineFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    
    // Modal states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedProject, setSelectedProject] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    
    // Form state
    const [formData, setFormData] = useState({
        company_id: '',
        magazine_id: '',
        contact_id: '',
        title: '',
        issue_label: '',
        deadline: '',
        notes: '',
    });

    useEffect(() => {
        fetchData();
    }, [magazineFilter, statusFilter]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (magazineFilter && magazineFilter !== 'all') {
                params.append('magazine_id', magazineFilter);
            }
            if (statusFilter && statusFilter !== 'all') {
                params.append('status', statusFilter);
            }
            
            const [projectsRes, magazinesRes, companiesRes] = await Promise.all([
                api.get(`/editorial-projects?${params.toString()}`),
                api.get('/magazines'),
                api.get('/companies'),
            ]);
            
            setProjects(projectsRes.data);
            setMagazines(magazinesRes.data);
            setCompanies(companiesRes.data);
        } catch (error) {
            console.error('Failed to fetch data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    };

    const fetchContactsForCompany = async (companyId) => {
        if (!companyId) {
            setContacts([]);
            return;
        }
        try {
            const response = await api.get(`/contacts?company_id=${companyId}`);
            setContacts(response.data);
        } catch (error) {
            console.error('Failed to fetch contacts:', error);
        }
    };

    const handleCreate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            const response = await api.post('/editorial-projects', formData);
            toast.success('Dossier magazine créé');
            setShowCreateModal(false);
            resetForm();
            fetchData();
            navigate(`/editorial/${response.data.id}`);
        } catch (error) {
            console.error('Failed to create project:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la création');
        } finally {
            setSubmitting(false);
        }
    };

    const handleUpdate = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        
        try {
            await api.patch(`/editorial-projects/${selectedProject.id}`, formData);
            toast.success('Dossier mis à jour');
            setShowEditModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to update project:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour');
        } finally {
            setSubmitting(false);
        }
    };

    const handleDelete = async () => {
        setSubmitting(true);
        
        try {
            await api.delete(`/editorial-projects/${selectedProject.id}`);
            toast.success('Dossier supprimé');
            setShowDeleteModal(false);
            fetchData();
        } catch (error) {
            console.error('Failed to delete project:', error);
            toast.error(error.response?.data?.detail || 'Erreur lors de la suppression');
        } finally {
            setSubmitting(false);
        }
    };

    const resetForm = () => {
        setFormData({
            company_id: '',
            magazine_id: '',
            contact_id: '',
            title: '',
            issue_label: '',
            deadline: '',
            notes: '',
        });
        setContacts([]);
    };

    const openEditModal = (project) => {
        setSelectedProject(project);
        setFormData({
            company_id: project.company_id,
            magazine_id: project.magazine_id,
            contact_id: project.contact_id || '',
            title: project.title,
            issue_label: project.issue_label || '',
            deadline: project.deadline || '',
            notes: project.notes || '',
        });
        fetchContactsForCompany(project.company_id);
        setShowEditModal(true);
    };

    const getStatusBadge = (status, progress) => {
        if (status === 'completed' || progress === 100) {
            return <Badge className="bg-green-500/10 text-green-500">Terminé</Badge>;
        }
        if (progress >= 70) {
            return <Badge className="bg-blue-500/10 text-blue-500">Finalisation</Badge>;
        }
        if (progress >= 30) {
            return <Badge className="bg-orange-500/10 text-orange-500">En cours</Badge>;
        }
        return <Badge className="bg-gray-500/10 text-gray-500">Démarré</Badge>;
    };

    const getMagazineBadge = (magazineLabel) => {
        const colors = {
            'Décideur': 'bg-purple-500/10 text-purple-500',
            'The Place to B': 'bg-blue-500/10 text-blue-500',
            "Le Mag' de l'Immo": 'bg-emerald-500/10 text-emerald-500',
        };
        return (
            <Badge className={colors[magazineLabel] || 'bg-gray-500/10 text-gray-500'}>
                {magazineLabel}
            </Badge>
        );
    };

    const filteredProjects = projects.filter(p => {
        if (!searchQuery) return true;
        const search = searchQuery.toLowerCase();
        return p.title.toLowerCase().includes(search) ||
               p.company_name?.toLowerCase().includes(search);
    });

    // Stats
    const totalProjects = projects.length;
    const completedProjects = projects.filter(p => p.progress_percent === 100).length;
    const inProgressProjects = projects.filter(p => p.progress_percent > 0 && p.progress_percent < 100).length;

    if (loading) {
        return (
            <div className="space-y-6">
                <div className="flex items-center justify-between">
                    <Skeleton className="h-8 w-48" />
                    <Skeleton className="h-10 w-40" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {[1, 2, 3].map((i) => (
                        <Skeleton key={i} className="h-24" />
                    ))}
                </div>
                <Skeleton className="h-96 w-full" />
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in">
            {/* Header */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-2xl lg:text-3xl font-bold font-heading">Dossiers Magazine</h1>
                    <p className="text-muted-foreground mt-1">
                        Suivi éditorial des insertions magazine
                    </p>
                </div>
                <Button
                    onClick={() => {
                        resetForm();
                        setShowCreateModal(true);
                    }}
                    className="rounded-full"
                    data-testid="create-editorial-btn"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    Nouveau dossier
                </Button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Total dossiers</p>
                                <p className="text-2xl font-bold">{totalProjects}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-primary/10">
                                <BookOpen className="w-5 h-5 text-primary" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">En cours</p>
                                <p className="text-2xl font-bold">{inProgressProjects}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-orange-500/10">
                                <FileText className="w-5 h-5 text-orange-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
                <Card>
                    <CardContent className="p-4">
                        <div className="flex items-center justify-between">
                            <div>
                                <p className="text-sm text-muted-foreground">Terminés</p>
                                <p className="text-2xl font-bold">{completedProjects}</p>
                            </div>
                            <div className="p-3 rounded-xl bg-green-500/10">
                                <CheckCircle2 className="w-5 h-5 text-green-500" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>

            {/* Filters */}
            <Card>
                <CardContent className="p-4">
                    <div className="flex flex-col sm:flex-row gap-4">
                        <div className="relative flex-1">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                            <Input
                                placeholder="Rechercher par titre ou entreprise..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="pl-9 rounded-lg"
                                data-testid="editorial-search-input"
                            />
                        </div>
                        <Select value={magazineFilter} onValueChange={setMagazineFilter}>
                            <SelectTrigger className="w-full sm:w-48 rounded-lg" data-testid="magazine-filter">
                                <BookOpen className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Magazine" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous les magazines</SelectItem>
                                {magazines.filter(m => m.is_active).map((m) => (
                                    <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        <Select value={statusFilter} onValueChange={setStatusFilter}>
                            <SelectTrigger className="w-full sm:w-40 rounded-lg">
                                <Filter className="w-4 h-4 mr-2" />
                                <SelectValue placeholder="Statut" />
                            </SelectTrigger>
                            <SelectContent>
                                <SelectItem value="all">Tous</SelectItem>
                                <SelectItem value="active">En cours</SelectItem>
                                <SelectItem value="completed">Terminés</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                </CardContent>
            </Card>

            {/* Projects Grid */}
            {filteredProjects.length === 0 ? (
                <Card>
                    <CardContent className="py-12">
                        <div className="text-center">
                            <BookOpen className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                            <p className="text-muted-foreground">Aucun dossier magazine</p>
                            <Button
                                variant="link"
                                onClick={() => {
                                    resetForm();
                                    setShowCreateModal(true);
                                }}
                                className="mt-2"
                            >
                                Créer un dossier
                            </Button>
                        </div>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredProjects.map((project) => (
                        <Card
                            key={project.id}
                            className="card-hover cursor-pointer"
                            onClick={() => navigate(`/editorial/${project.id}`)}
                            data-testid={`editorial-card-${project.id}`}
                        >
                            <CardContent className="p-5">
                                <div className="flex items-start justify-between mb-3">
                                    <div className="flex-1 min-w-0">
                                        <h3 className="font-semibold truncate">{project.title}</h3>
                                        <p className="text-sm text-muted-foreground flex items-center gap-1 mt-1">
                                            <Building2 className="w-3 h-3" />
                                            {project.company_name}
                                        </p>
                                    </div>
                                    <DropdownMenu>
                                        <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-full">
                                                <MoreHorizontal className="w-4 h-4" />
                                            </Button>
                                        </DropdownMenuTrigger>
                                        <DropdownMenuContent align="end">
                                            <DropdownMenuItem onClick={(e) => {
                                                e.stopPropagation();
                                                navigate(`/editorial/${project.id}`);
                                            }}>
                                                <Eye className="w-4 h-4 mr-2" />
                                                Voir
                                            </DropdownMenuItem>
                                            <DropdownMenuItem onClick={(e) => {
                                                e.stopPropagation();
                                                openEditModal(project);
                                            }}>
                                                <Pencil className="w-4 h-4 mr-2" />
                                                Modifier
                                            </DropdownMenuItem>
                                            {isSuperAdmin() && (
                                                <DropdownMenuItem
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        setSelectedProject(project);
                                                        setShowDeleteModal(true);
                                                    }}
                                                    className="text-destructive focus:text-destructive"
                                                >
                                                    <Trash2 className="w-4 h-4 mr-2" />
                                                    Supprimer
                                                </DropdownMenuItem>
                                            )}
                                        </DropdownMenuContent>
                                    </DropdownMenu>
                                </div>

                                <div className="flex items-center gap-2 mb-4">
                                    {getMagazineBadge(project.magazine_label)}
                                    {project.issue_label && (
                                        <Badge variant="outline">{project.issue_label}</Badge>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <div className="flex items-center justify-between text-sm">
                                        <span className="text-muted-foreground">Progression</span>
                                        <span className="font-medium">{project.progress_percent}%</span>
                                    </div>
                                    <Progress value={project.progress_percent} className="h-2" />
                                </div>

                                <div className="flex items-center justify-between mt-4 pt-3 border-t">
                                    {getStatusBadge(project.status, project.progress_percent)}
                                    {project.deadline && (
                                        <span className="text-xs text-muted-foreground flex items-center gap-1">
                                            <Calendar className="w-3 h-3" />
                                            {new Date(project.deadline).toLocaleDateString('fr-FR')}
                                        </span>
                                    )}
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            {/* Create/Edit Modal */}
            <Dialog open={showCreateModal || showEditModal} onOpenChange={(open) => {
                if (!open) {
                    setShowCreateModal(false);
                    setShowEditModal(false);
                    resetForm();
                }
            }}>
                <DialogContent className="max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading">
                            {showEditModal ? 'Modifier le dossier' : 'Nouveau dossier magazine'}
                        </DialogTitle>
                        <DialogDescription>
                            {showEditModal ? 'Mettez à jour les informations' : 'Créez un dossier de suivi éditorial'}
                        </DialogDescription>
                    </DialogHeader>

                    <form onSubmit={showEditModal ? handleUpdate : handleCreate} className="space-y-4">
                        <div className="space-y-2">
                            <Label>Entreprise *</Label>
                            <SearchableCombobox
                                options={companies.map(c => ({ id: c.id, label: c.legal_name, sublabel: c.city || c.sector }))}
                                value={formData.company_id}
                                onValueChange={(v) => {
                                    setFormData({ ...formData, company_id: v, contact_id: '' });
                                    fetchContactsForCompany(v);
                                }}
                                placeholder="Rechercher une entreprise..."
                                emptyMessage="Aucune entreprise trouvée"
                                type="company"
                                data-testid="editorial-company-select"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label>Magazine *</Label>
                            <Select
                                value={formData.magazine_id}
                                onValueChange={(v) => setFormData({ ...formData, magazine_id: v })}
                                required
                            >
                                <SelectTrigger className="rounded-lg" data-testid="editorial-magazine-select">
                                    <SelectValue placeholder="Sélectionner un magazine" />
                                </SelectTrigger>
                                <SelectContent>
                                    {magazines.filter(m => m.is_active).map((m) => (
                                        <SelectItem key={m.id} value={m.id}>{m.label}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        {formData.company_id && (
                            <div className="space-y-2">
                                <Label>Contact interlocuteur</Label>
                                <SearchableCombobox
                                    options={contacts.map(c => ({ id: c.id, label: `${c.first_name} ${c.last_name}`, sublabel: c.job_title }))}
                                    value={formData.contact_id}
                                    onValueChange={(v) => setFormData({ ...formData, contact_id: v })}
                                    placeholder="Rechercher un contact..."
                                    emptyMessage="Aucun contact trouvé"
                                    type="contact"
                                    disabled={!formData.company_id}
                                />
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label>Titre du dossier *</Label>
                            <Input
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="Ex: Portrait dirigeant Q1 2026"
                                required
                                className="rounded-lg"
                                data-testid="editorial-title-input"
                            />
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Édition / Numéro</Label>
                                <Input
                                    value={formData.issue_label}
                                    onChange={(e) => setFormData({ ...formData, issue_label: e.target.value })}
                                    placeholder="Ex: N°45 - Mars 2026"
                                    className="rounded-lg"
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Date limite</Label>
                                <Input
                                    type="date"
                                    value={formData.deadline}
                                    onChange={(e) => setFormData({ ...formData, deadline: e.target.value })}
                                    className="rounded-lg"
                                />
                            </div>
                        </div>

                        <div className="space-y-2">
                            <Label>Notes</Label>
                            <Textarea
                                value={formData.notes}
                                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                                rows={3}
                                placeholder="Informations complémentaires..."
                                className="rounded-lg"
                            />
                        </div>

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => {
                                    setShowCreateModal(false);
                                    setShowEditModal(false);
                                    resetForm();
                                }}
                                className="rounded-full"
                            >
                                Annuler
                            </Button>
                            <Button type="submit" disabled={submitting} className="rounded-full" data-testid="editorial-submit-btn">
                                {submitting ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                        {showEditModal ? 'Mise à jour...' : 'Création...'}
                                    </>
                                ) : (
                                    showEditModal ? 'Mettre à jour' : 'Créer'
                                )}
                            </Button>
                        </DialogFooter>
                    </form>
                </DialogContent>
            </Dialog>

            {/* Delete Confirmation Modal */}
            <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle className="font-heading">Confirmer la suppression</DialogTitle>
                        <DialogDescription>
                            Êtes-vous sûr de vouloir supprimer le dossier "{selectedProject?.title}" ?
                            Cette action est irréversible.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button
                            variant="outline"
                            onClick={() => setShowDeleteModal(false)}
                            className="rounded-full"
                        >
                            Annuler
                        </Button>
                        <Button
                            variant="destructive"
                            onClick={handleDelete}
                            disabled={submitting}
                            className="rounded-full"
                        >
                            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Supprimer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default Editorial;
